import { createFileRoute } from '@tanstack/react-router';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { useSelectedSubscription } from '@/lib/use-selected-subscription';
import { useTranslation } from 'react-i18next';
import { useBackTo } from '@/lib/use-back';
import { formatDistanceToNow } from 'date-fns';
import { ru, enUS } from 'date-fns/locale';
import { CheckCircle2, XCircle, Activity as ActivityIcon } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { IconChip } from '@/components/ui/calm';
import { SkeletonRows } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { useState } from 'react';

export const Route = createFileRoute('/activity')({
  component: Activity,
});

type FilterValue = 'all' | 'success' | 'failed';

function Activity() {
  useBackTo('/profile');
  const { t, i18n } = useTranslation();
  const { sub, isPending: subPending } = useSelectedSubscription();
  const { data: logs, isPending: logsPending } = useQuery({
    queryKey: ['activity', sub?.id],
    queryFn: () => (sub ? api.activity(sub.id) : Promise.resolve([])),
    enabled: !!sub,
    placeholderData: keepPreviousData,
  });
  // Имена устройств: в журнале у поставщика только номер устройства и HWID, а HWID это
  // 64 символа хэша, по которому своё устройство не узнать. Тот же запрос, что на
  // экране «Устройства», поэтому из кэша.
  const { data: devices } = useQuery({
    queryKey: ['devices', sub?.id],
    queryFn: () => (sub ? api.devices(sub.id) : Promise.resolve([])),
    enabled: !!sub,
  });
  const deviceNames = new Map((devices ?? []).map((d) => [d.id, d.name]));
  const [filter, setFilter] = useState<FilterValue>('all');

  // Без этого пустой `logs` во время загрузки показывал «Пока нет подключений» —
  // человек читал ложный ответ вместо ожидания. Запрос логов ждёт подписку
  // (`enabled: !!sub`), поэтому его pending учитываем только когда она есть.
  const loading = subPending || (!!sub && logsPending);

  const filteredLogs = logs?.filter((log) => filter === 'all' || log.status === filter) || [];
  const locale = i18n.language === 'ru' ? ru : enUS;

  const filters: { key: FilterValue; label: string }[] = [
    { key: 'all', label: t('Filter all') },
    { key: 'success', label: t('Filter success') },
    { key: 'failed', label: t('Filter failed') },
  ];

  return (
    <div className="p-4 pb-24 space-y-6">
      <div className="px-2">
        <h2 className="tl-h text-[32px] font-semibold leading-tight tracking-[-0.035em] mb-1 text-foreground">{t('Connections')}</h2>
        <p className="text-sm text-[var(--t-muted)] mb-4">{t('Connections desc')}</p>
        <div className="flex gap-2">
          {filters.map((f) => (
            <button
              type="button"
              key={f.key}
              onClick={() => setFilter(f.key)}
              className={cn(
                'px-4 py-1.5 rounded-full text-xs font-semibold transition-transform duration-100 active:scale-[0.97]',
                filter === f.key ? 'border-transparent bg-foreground text-background' : 'calm-card text-[var(--t-muted)]',
              )}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      <div className="space-y-3">
        {loading ? (
          <SkeletonRows count={4} leadingClassName="w-6 h-6 rounded-full" itemClassName="rounded-[var(--r-card)] p-4 gap-4" />
        ) : filteredLogs.length === 0 ? (
          <div className="flex flex-col items-center justify-center p-8 text-center calm-card rounded-[var(--r-card)]">
            <IconChip icon={ActivityIcon} tone="blue" size={64} radius={18} className="mb-4" />
            <h3 className="font-semibold mb-1 text-foreground">{t('No activity')}</h3>
            <p className="text-sm text-[var(--t-muted)]">{t('No activity desc')}</p>
          </div>
        ) : (
          filteredLogs.map((log) => (
            <Card key={log.id} className="calm-card">
              <CardContent className="p-4 flex items-start gap-4">
                {log.status === 'success' ? (
                  <CheckCircle2 className="text-[var(--t-ok)] w-6 h-6 shrink-0" />
                ) : (
                  <XCircle className="text-[var(--t-danger)] w-6 h-6 shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm truncate text-foreground">
                    {deviceNames.get(log.deviceId) ||
                      (log.deviceName ? t('Device hwid', { id: log.deviceName.slice(0, 8) }) : t('Device unknown'))}
                  </div>
                  {/* IP целиком: ради него экран и открывают («видите чужой IP»), а IPv6
                      длиной до 39 знаков обрезался до «2a02:2698:…». */}
                  <div className="text-[11px] text-[var(--t-muted)] [overflow-wrap:anywhere]" style={{ fontFamily: 'var(--font-mono)' }}>{log.ip || '—'}</div>
                  {/* Время строкой ниже, а не колонкой справа: «около 18 часов назад»
                      забирало половину ширины, и имя устройства резалось на 320px. */}
                  <div className="text-[11px] text-[#7c7e86] mt-0.5">
                    {formatDistanceToNow(new Date(log.timestamp), { addSuffix: true, locale })}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
}
